import React, { useState, useEffect, useRef } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { loadingStatus, getPayload } from "../../actions"
import _ from "lodash"
import settings from "settings"
import { toast } from "react-toastify"
import axios from "axios"
import InfoWindow from "containers/mapping/infoWindowMapbox"
import * as mapboxgl from "mapbox-gl"
import center from "@turf/center"
import getData from "helpers/asyncSubmitData"
import LotModal from "containers/modals/lot"
import Switch from "react-switch"
import { polygon, point } from "@turf/helpers"
import {
	nearestPointOnLine,
	lineString,
	centerOfMass,
	explode,
	booleanOverlap,
	lineIntersect,
	lineSplit,
	booleanPointInPolygon,
} from "@turf/turf"
import distance from "@turf/distance"
import helpers from "helpers/helpers"
mapboxgl.accessToken = settings.mapboxKey

function ArcRepetitionMap(props) {
	const [ready, setReady] = useState(false)
	const mapContainer = useRef(null)
	const [modal, setModal] = useState(false)
	const [map, setMap] = useState(null)
	const [data, setData] = useState(false)
	const [geojson, setGeojson] = useState(null)
	const [lat, setLat] = useState("")
	const [lng, setLng] = useState("")
	const [arcData, setArcData] = useState({})
	const [zoom, setZoom] = useState(16)
	const [lot, setLot] = useState(null)
	const [hoveringIssue, setHoveringIssue] = useState(false)
	const [blockers, setBlockers] = useState([])
	const [showAerial, setShowAerial] = useState(false)
	const [aerialData, setAerialData] = useState(null)
	const [sectionLabels, setSectionLabels] = useState([])
	const [sectionGeo, setSectionGeo] = useState(null)

	useEffect(() => {
		build()
	}, [])

	useEffect(() => {
		if (ready) {
			initMap()
		}
	}, [ready])

	useEffect(() => {
		// For the submittal process, we need to update the map when the floorplan changes
		if (props?.data?.floorplan && props?.data?.floorplan > 0 && props.submit) {
			updateMapData()
		}
		buildBlockers()
	}, [props?.data?.floorplan, props?.data?.elevation])

	useEffect(() => {
		if (data) {
			initGeo()
		}
	}, [data])

	useEffect(() => {
		issueLine()
	}, [hoveringIssue])

	useEffect(() => {
		if (blockers.length > 0 && props?.setBlocking) props.setBlocking(true)
		else if (props?.setBlocking) props.setBlocking(false)
	}, [blockers])

	useEffect(() => {
		if (!map || !map.getLayer("aerial")) return
		if (showAerial) {
			if (map.getLayer("aerial")) {
				map.setLayoutProperty("aerial", "visibility", "visible")
			}
		} else {
			map.setLayoutProperty("aerial", "visibility", "none")
		}
	}, [showAerial, map])

	useEffect(() => {
		if (map && sectionLabels.length > 0) {
			addSectionLabels()
		}
	}, [map, sectionLabels])

	useEffect(() => {
		if (map && sectionGeo) {
			addSectionOutlines()
		}
	}, [map, sectionGeo])

	const polygonCenter = (coordinates) => {
		if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
			return null
		}

		let x = 0
		let y = 0
		let validPoints = 0

		// Filter out invalid coordinates and count valid ones
		for (let i = 0; i < coordinates.length; i++) {
			const coord = coordinates[i]
			if (
				Array.isArray(coord) &&
				coord.length >= 2 &&
				typeof coord[0] === "number" &&
				typeof coord[1] === "number" &&
				!isNaN(coord[0]) &&
				!isNaN(coord[1])
			) {
				x += coord[0]
				y += coord[1]
				validPoints++
			}
		}

		if (validPoints === 0) {
			return null
		}

		return {
			lng: x / validPoints,
			lat: y / validPoints,
		}
	}

	const addSectionLabels = () => {
		if (!map) return

		// Remove existing section markers if any
		const existingMarkers = document.querySelectorAll(".section-label")
		existingMarkers.forEach((marker) => {
			const parent = marker.parentElement
			if (parent && parent._marker) {
				parent._marker.remove()
			}
		})

		// Add section number labels with black backgrounds
		sectionLabels.forEach((label) => {
			// Skip labels with invalid coordinates
			if (
				!label.coordinates ||
				!Array.isArray(label.coordinates) ||
				label.coordinates.length !== 2 ||
				isNaN(label.coordinates[0]) ||
				isNaN(label.coordinates[1])
			) {
				console.log("Skipping invalid label coordinates:", label)
				return
			}

			const labelElement = document.createElement("div")
			labelElement.className = "section-label"
			labelElement.textContent = label.text
			labelElement.style.fontWeight = "bold"
			labelElement.style.textShadow = "1px 1px 2px rgba(0,0,0,0.3)"
			labelElement.style.color = "white"
			labelElement.style.backgroundColor = "#111"
			labelElement.style.padding = "3px 6px"
			labelElement.style.borderRadius = "3px"
			labelElement.style.fontSize = "12px"
			labelElement.style.pointerEvents = "none"
			labelElement.style.zIndex = "1000"

			const marker = new mapboxgl.Marker({
				element: labelElement,
				anchor: "center",
			})
				.setLngLat(label.coordinates)
				.addTo(map)

			// Store marker reference for cleanup
			labelElement.parentElement._marker = marker
		})
	}

	const addSectionOutlines = () => {
		if (!map || !sectionGeo) return

		// Remove existing section layers if any
		if (map.getLayer("sections-outline")) {
			map.removeLayer("sections-outline")
		}
		if (map.getSource("sections")) {
			map.removeSource("sections")
		}

		// Add the section data source
		map.addSource("sections", {
			type: "geojson",
			data: sectionGeo,
		})

		// Add the section outline layer with black stroke, no fill
		map.addLayer({
			id: "sections-outline",
			type: "line",
			source: "sections",
			paint: {
				"line-color": "#000000", // Black stroke
				"line-width": 2, // 3 pixel width as requested
				"line-opacity": 0.3, // Slightly transparent
			},
		})
	}

	const getSectionData = async (propertyId) => {
		try {
			const response = await getData("getProperty", { id: propertyId })
			if (response.data.section_geo) {
				// Store the section geometry for outlines
				setSectionGeo(response.data.section_geo)

				// Process section labels
				let sectionLabels = []
				response.data.section_geo.features.forEach((feature) => {
					let center = polygonCenter(feature.geometry.coordinates[0])
					if (center) {
						let sec = _.find(props.payload.public.sections, { id: feature.properties.Id })
						if (sec) {
							sectionLabels.push({
								text: sec.name,
								coordinates: [center.lng, center.lat],
							})
						}
					}
				})
				setSectionLabels(sectionLabels)
			}
		} catch (e) {
			console.log("Section data fetch error:", e)
		}
	}

	const build = async () => {
		let lot = _.find(props.payload.exec.lots, { id: props.id })
		if (lot) {
			setData(lot)
			setArcData(lot.arc_data)
			setLat(lot.latitude)
			setLng(lot.longitude)

			// Fetch section data for the property
			if (props.activeProperties.length > 0) {
				await getSectionData(props.activeProperties[0])
			}
		} else {
			setData(false)
		}
	}

	const issueLine = () => {
		if (!map) return
		try {
			if (hoveringIssue) {
				let mapLot = _.find(geojson?.features, { properties: { id: hoveringIssue } })
				let selectedLot = _.find(geojson?.features, { properties: { id: data.id } })

				// Get the center points of both polygons
				const polygon1 = polygon(mapLot.geometry.coordinates)
				const polygon2 = polygon(selectedLot.geometry.coordinates)

				const center1 = centerOfMass(polygon1).geometry.coordinates
				const center2 = centerOfMass(polygon2).geometry.coordinates

				// Create a direct line between the centers
				const line = lineString([center1, center2])

				// Add the line to the map
				if (map.getLayer("route")) map.removeLayer("route")
				if (map.getSource("route")) map.removeSource("route")

				map.addSource("route", {
					type: "geojson",
					data: line,
				})

				map.addLayer({
					id: "route",
					type: "line",
					source: "route",
					layout: {
						"line-join": "round",
						"line-cap": "round",
					},
					paint: {
						"line-color": "black",
						"line-width": 3,
					},
				})
			} else {
				if (map.getLayer("route")) map.removeLayer("route")
				if (map.getSource("route")) map.removeSource("route")
			}
		} catch (e) {
			console.log(e)
		}
	}

	const initMap = () => {
		if (map) {
			// on submit we're refreshing the map, so we need to remove the old layers
			map.removeLayer("lots")
			map.removeLayer("lines")
			map.removeLayer("streetNums")
			map.removeSource("lots")
			map.removeSource("streetNums")
		}
		if (props.activeProperties.length == 1 && props.payloadStatus && mapContainer.current && geojson) {
			let center = [lng, lat]
			if (!lat && !lng) {
				let property = _.find(props.payload.public.properties, { id: props.activeProperties[0] })
				center = [property.lng, property.lat]
			}
			const map = new mapboxgl.Map({
				container: mapContainer.current,
				//style: "mapbox://styles/mapbox/streets-v11",
				center,
				pitch: 0,
				zoom,
				minZoom: 13,
				maxZoom: 18,
				attributionControl: false,
				preserveDrawingBuffer: true, // added for PDF/JPG export ability
			})
			// Add navigation control (the +/- zoom buttons)
			map.addControl(new mapboxgl.NavigationControl(), "bottom-right")

			map.on("move", () => {
				//setLng(map.getCenter().lng.toFixed(4))
				//setLat(map.getCenter().lat.toFixed(4))
				//setZoom(map.getZoom().toFixed(2))
			})
			map.on("load", async () => {
				map.setLayoutProperty("building-number-label", "visibility", "none")
				try {
					_.forEach(geojson.features, (v) => {
						let num = v.properties.address.split(" ")
						v.properties.street_number = num[0]
					})
				} catch (e) {
					console.error(e)
				}
				let centers = _.cloneDeep(geojson)
				centers = buildCenters(centers)
				// check if lots source exists already, if it doesn't, add everything.  used switching between satellite and street.
				if (!map.getSource("lots")) {
					map.addSource("lots", {
						type: "geojson",
						data: geojson,
					})
					map.addSource("streetNums", {
						type: "geojson",
						data: centers,
					})
					// Fill in the lots
					map.addLayer({
						id: "lots",
						type: "fill",
						source: "lots",
						paint: {
							//"fill-outline-color": "#FF0000",
							"fill-color": [
								"case",
								["==", ["get", "submittedLot"], true], // the active lot
								"#214aed",
								["has", "issueScore"], // lot has issue score - use color based on score
								[
									"case",
									["<", ["get", "issueScore"], 30],
									"#4a8e00", // Dark green for low risk (0-29)
									["<", ["get", "issueScore"], 60],
									"#D97C23", // Orange for medium risk (30-59)
									["<", ["get", "issueScore"], 80],
									"#ff8c00", // Dark orange for medium-high risk (60-79)
									["<", ["get", "issueScore"], 90],
									"#DC504B", // Red for very high risk (80-89)
									"#ff4500", // Red-orange for critical risk (90+)
								],
								"transparent",
							],
							"fill-opacity": [
								"case",
								["==", ["get", "submittedLot"], true], // the active lot
								0.8,
								["has", "issueScore"], // lot has issue score - make it visible
								1.0,
								0,
							],
						},
					})
					// Add white line outlines
					map.addLayer({
						id: "lines",
						type: "line",
						source: "lots",
						paint: {
							"line-width": 2,
							"line-color": "#FFF",
							"line-opacity": 1,
						},
					})
					// Street numbers
					map.addLayer({
						id: "streetNums",
						type: "symbol",
						source: "streetNums",
						minzoom: 17,
						layout: {
							"text-field": ["get", "street_number"],
							"text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
							//"text-offset": [0, 1.25],
							//"text-anchor": "top",
							"text-size": 10,
						},
						paint: {
							"text-color": "#777",
						},
					})
				}
				//console.log(map.getStyle().layers)
				map.on("mouseenter", "lots", (e) => {})
				map.on("click", "lots", (e) => {
					let l = e.features[0].properties
					//let lot = _.find(props.payload.exec.lots, { id: l.id })
					l.sales = JSON.parse(l.sales)
					//setLot(lot)
					setLot(l)
					//setLotOpen(true)
				})
				//Aerial check
				if (props.payload.satellite?.length) {
					let shots = _.filter(props.payload.satellite, { property: props.activeProperties[0] })
					let latest = _.maxBy(shots, "capture_time")
					if (latest) {
						// Add raster tileset layer
						map.addSource("aerial", {
							type: "raster",
							url: `mapbox://${latest.tileset}`, // Replace with your tileset ID
							//tileSize: 256,
						})
						map.addLayer({
							id: "aerial",
							type: "raster",
							source: "aerial",
							minzoom: 0,
							maxzoom: 22,
							paint: {
								"raster-opacity": 0.8,
								"raster-fade-duration": 0,
							},
							slot: "bottom",
						})
					}
					setShowAerial(true)
					setAerialData(latest)
				}
				props.loadingStatus(false)
				setMap(map)
			})
			//map.scrollZoom.disable()
			// Clean up on unmount
			return () => map.remove()
		}
	}

	// Helper function to get issue score color based on score range
	const getIssueScoreColor = (score) => {
		if (score >= 0 && score < 30) return "#4a8e00" // Dark green for low risk
		else if (score >= 30 && score < 60) return "#D97C23" // Orange for medium risk
		else if (score >= 60 && score < 80) return "#ff8c00" // Dark orange for medium-high risk
		else if (score >= 80 && score < 90) return "#DC504B" // Red for very high risk
		else if (score >= 90) return "#ff4500" // Red-orange for critical risk
		return "transparent" // No fill for lots without issues
	}

	const buildGeoJson = async () => {
		let property = _.find(props.payload.public.properties, { id: props.activeProperties[0] })
		if (property) {
			let geo = await getGeo(property.id)
			let model = {
				type: "FeatureCollection",
				crs: {
					type: "name",
					properties: {
						name: "urn:ogc:def:crs:OGC:1.3:CRS84",
					},
				},
				features: [],
			}
			let chosenFP
			if (arcData?.floorplan) {
				chosenFP = _.find(props.payload.public.floorplans, { id: arcData.floorplan })
			} else if (props.data.floorplan) {
				chosenFP = _.find(props.payload.public.floorplans, { id: props.data.floorplan })
			}

			// Calculate issue scores for all lots first
			let issueScores = {}
			if (data) {
				let lot = _.find(geo.geo.features, (v) => v.properties.id === props.id)
				if (lot) {
					try {
						let lotPoly = polygon(lot.geometry.coordinates)
						let lotCenter = center(lotPoly).geometry.coordinates
						let params = {
							floorplan: false,
							elevation: false,
							brick: false,
							stone: false,
							trim: false,
							paint: false,
							siding: false,
							siding_type: false,
							garage_color: false,
							stucco: false,
							accent_color: false,
							shutters: false,
							exterior_other: false,
							style: false,
						}

						_.forEach(geo.geo.features, (v) => {
							if (props.id === v.properties.id) return
							if (v.properties.id !== props.id) {
								let vPoly = polygon(v.geometry.coordinates)
								let vCenter = center(vPoly).geometry.coordinates
								let dist = distance(point(lotCenter), point(vCenter), { units: "feet" })
								if (dist <= 500) {
									let model = _.clone(params)
									let hasIssues = false
									let bucket
									if (props.interface === "entry") {
										bucket = props.data
									} else bucket = arcData
									let floorplanMatch = false
									_.forEach(model, (val, key) => {
										if (key === "floorplan") {
											if (bucket[key] && bucket[key] === v.properties[key]) {
												model[key] = true
												hasIssues = true
												floorplanMatch = true
											}
										} else if (key === "elevation") {
											if (
												bucket[key] &&
												bucket[key] == v.properties[key] &&
												bucket["elevation"] == v.properties["elevation"]
											) {
												model[key] = true
												hasIssues = true
											}
										} else {
											let val = bucket[key]
											if (
												val &&
												typeof val === "string" &&
												(val.toLowerCase() === "n/a" ||
													val.toLowerCase() === "na" ||
													val.toLowerCase() === "none")
											)
												return
											if (typeof v.properties[key] === "number") {
												let lookupBucket = props.payload.public.arc_materials
												let colors = helpers.arcColorTypes()
												if (colors.includes(key)) {
													lookupBucket = props.payload.public.arc_colors
												}
												let lookup = _.find(lookupBucket, {
													id: v.properties[key],
												})
												if (lookup) {
													v.properties[key] = lookup.name
												}
											}
											if (typeof val === "number") {
												let lookupBucket = props.payload.public.arc_materials
												let colors = helpers.arcColorTypes()
												if (colors.includes(key)) {
													lookupBucket = props.payload.public.arc_colors
												}
												let lookup = _.find(lookupBucket, {
													id: val,
												})
												if (lookup) {
													val = lookup.name
												}
											}
											let similarScore = helpers.stringSimilarity(val, v.properties[key])
											if (similarScore > 90) {
												model[key] = true
												hasIssues = true
											}
										}
									})
									if (hasIssues) {
										let score = calculateSimilarityScore(model, dist, v.properties)
										issueScores[v.properties.id] = score
									}
								}
							}
						})
					} catch (e) {
						console.log(e)
					}
				}
			}

			_.forEach(geo.geo.features, (v, k) => {
				let hasSimilar = false
				// Check for similar on th chosen plan and see if this lots FP is in the similar list
				if (chosenFP?.similar && chosenFP.similar.includes(v.properties.floorplan)) {
					hasSimilar = true
				}
				if (!hasSimilar) {
					// not similar yet, check the other way
					if (v.properties.floorplan) {
						let fp = _.find(props.payload.public.floorplans, { id: v.properties.floorplan })
						if (fp && fp.similar && fp.similar.includes(chosenFP?.id)) {
							hasSimilar = true
						}
					}
				}
				let fp = data?.arc_data?.floorplan
				let elevation = data?.arc_data?.elevation
				if (props.submit) {
					fp = props.data.floorplan
					elevation = props.data.elevation
				}
				if (!v.geometry.coordinates.length) {
					return
				}
				if (typeof elevation === "number") elevation = elevation.toString()
				
				// Add issue score to lot properties
				if (issueScores[v.properties.id]) {
					v.properties.issueScore = issueScores[v.properties.id]
				}

				if (props.id === v.properties.id) {
					v.properties.submittedLot = true
				} else if (
					v.properties.floorplan === fp &&
					typeof v.properties?.elevation === "string" &&
					v.properties?.elevation?.toLowerCase() === elevation?.toLowerCase()
				) {
					v.properties.replication = 1
				} else if (v.properties.floorplan === fp) {
					v.properties.replication = 0.3
				} else if (hasSimilar) {
					v.properties.isSimilar = true
					v.properties.replication = 0.3
				}
				model.features.push(v)
			})
			return model
		} else {
			toast.error("Cannot find property.")
		}
	}

	const coordinatesCenter = (arr) => {
		let tmp = {
			lx: null,
			hx: null,
			ly: null,
			hy: null,
		}

		_.forEach(arr, (v) => {
			if (v[0] < tmp.lx || !tmp.lx) {
				tmp.lx = v[0]
			}
			if (v[0] > tmp.hx || !tmp.hx) {
				tmp.hx = v[0]
			}

			if (v[1] < tmp.ly || !tmp.ly) {
				tmp.ly = v[1]
			}
			if (v[1] > tmp.hy || !tmp.hy) {
				tmp.hy = v[1]
			}
		})
		let center = {}
		center.x = tmp.lx + (tmp.hx - tmp.lx) / 2
		center.y = tmp.ly + (tmp.hy - tmp.ly) / 2
		return center
	}

	let buildCenters = (data) => {
		// Mapbox's getCenter method wasn't producing good results.
		_.forEach(data.features, (v) => {
			const center = coordinatesCenter(v.geometry.coordinates[0])
			v.geometry = {
				type: "Point",
				coordinates: [center.x, center.y],
			}
			return v
		})

		return data
	}

	const getGeo = async (property) => {
		var user = localStorage.getItem("user")
		return new Promise((resolve, reject) => {
			axios
				.post(settings.ep + "/getGeo", { property: property }, { headers: { Authorization: `Bearer ${user}` } })
				.then((response) => {
					if (response.data.status == 200) {
						resolve(response.data)
					} else {
						let err = new Error()
						err.message = response.data.message
						err.status = response.data.status
						reject(err)
					}
				})
				.catch((error) => {
					reject(error)
				})
		})
	}

	const updateMapData = async () => {
		// Update only the data without recreating the map
		if (map && map.getSource("lots")) {
			let geojson = await buildGeoJson()
			setGeojson(geojson)
			
			// Update the existing data sources
			map.getSource("lots").setData(geojson)
			
			// Update street numbers
			let centers = _.cloneDeep(geojson)
			centers = buildCenters(centers)
			map.getSource("streetNums").setData(centers)
		} else {
			// If map doesn't exist yet, use the original method
			initGeo()
		}
	}

	const initGeo = async () => {
		setReady(false)
		let geojson = await buildGeoJson()
		setGeojson(geojson)
		setReady(true)
	}

	const Legend = () => {
		return (
			<div className={`flex justify-between`}>
				<div className="grid grid-cols-3 gap-1 text-xs py-2 w-full">
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#4a8e00" }}>
						Low Risk (0-29)
					</div>
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#D97C23" }}>
						Medium Risk (30-59)
					</div>
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#ff8c00" }}>
						High Risk (60-79)
					</div>
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#DC504B" }}>
						Very High Risk (80-89)
					</div>
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#ff4500" }}>
						Critical Risk (90+)
					</div>
					<div className="px-2 py-1 text-center text-white font-bold" style={{ backgroundColor: "#214aed" }}>
						Selected Lot
					</div>
				</div>
				{aerialData && (
					<div className="switch pl-2" onClick={(e) => e.stopPropagation()}>
						<Switch
							onChange={() => setShowAerial(!showAerial)}
							checked={showAerial}
							checkedIcon={false}
							uncheckedIcon={false}
							key={"switch"}
							id={"switch"}
							height={16}
							width={30}
						/>
						<p className="text-xs">{showAerial ? "Aerial" : "No Aerial"}</p>
					</div>
				)}
			</div>
		)
	}

	function calculateSimilarityScore(model, distance, lot) {
		const params = [
			"floorplan",
			"elevation",
			"brick",
			"stone",
			"trim",
			"paint",
			"siding",
			"garage_color",
			"stucco",
			"accent_color",
			"shutters",
			"exterior_other",
			"style",
		]
		let lotSize = lot.lot_size
		const weights = {
			floorplan: 5,
			elevation: 5,
		}
		let totalWeight = 0
		let score = 0
		let matchCount = 0
		params.forEach((param) => {
			let weight = weights[param] || 1
			// Special handling for elevation
			if (param === "elevation") {
				if (!model.floorplan) {
					weight = 0
				}
			}
			totalWeight += weight
			if (model[param] === true) {
				score += weight
				matchCount++
			}
		})
		// Calculate distance factor (inverse relationship)
		// Assuming max distance is 500 feet, min distance is 0 feet
		const distanceFactor = 1 - distance / 400
		// Add distance to total weight
		totalWeight += 5 // Giving distance a weight of 5
		// Add distance score
		score += 5 * distanceFactor
		//console.log(score, totalWeight, score/ totalWeight)

		// Normalize score to 1-100 scale
		let normalizedScore = (score / totalWeight) * 99 + 1

		// Enhanced scoring for floorplan and elevation matches based on proximity
		if (model.floorplan && model.elevation && distance <= 300) {
			// Base minimum score of 80 for floorplan + elevation
			let baseScore = 80
			
			// Increase score based on proximity - closer lots get higher scores
			if (distance <= 100) {
				baseScore = 95 // Very close lots with same plan + elevation are critical
			} else if (distance <= 200) {
				baseScore = 90 // Close lots get very high scores
			} else if (distance <= 250) {
				baseScore = 85 // Medium close lots get high scores
			}
			
			normalizedScore = Math.max(normalizedScore, baseScore)
		}
		// Apply minimum score of 60 if only floorplan matches and distance is within 300 feet
		else if (model.floorplan && distance <= 300) {
			normalizedScore = Math.max(normalizedScore, 60)
		}

		// Add additional points for matches beyond floorplan and elevation
		// Each additional match adds 5 points, up to a maximum of 20 additional points
		let additionalMatches = matchCount - (model.floorplan ? 1 : 0) - (model.elevation ? 1 : 0)
		let additionalPoints = Math.min(additionalMatches * 5, 20)
		normalizedScore += additionalPoints

		// Apply rule: if only one issue and it's NOT floorplan or elevation, cap at 19
		if (matchCount === 1 && !model.floorplan && !model.elevation) {
			normalizedScore = Math.min(normalizedScore, 19)
		}

		// Ensure the score is between 1 and 100
		normalizedScore = Math.max(normalizedScore, 1) // Cannot be less than 1
		normalizedScore = Math.min(normalizedScore, 100) // Cannot exceed 100

		return Math.round(normalizedScore)
	}

	const buildIssues = () => {
		let ret = []
		let lot = _.find(geojson?.features, (v) => {
			return v.properties.id === props.id
		})
		if (lot) {
			try {
				let lotPoly = polygon(lot.geometry.coordinates)
				let lotCenter = center(lotPoly).geometry.coordinates
				let params = {
					floorplan: false,
					elevation: false,
					brick: false,
					stone: false,
					trim: false,
					paint: false,
					siding: false,
					siding_type: false,
					garage_color: false,
					//roof_color: false,
					stucco: false,
					accent_color: false,
					shutters: false,
					exterior_other: false,
					style: false,
				}

				// Now go through the geo and find the lots that are within X feet
				let issues = []
				_.forEach(geojson.features, (v) => {
					if (props.id === v.properties.id) return
					//? v are all the lots on the map
					if (v.properties.id !== props.id) {
						let vPoly = polygon(v.geometry.coordinates)
						let vCenter = center(vPoly).geometry.coordinates
						let dist = distance(point(lotCenter), point(vCenter), { units: "feet" })
						if (dist <= 500) {
							//? Run through the properties on these lots and compile issues, if any
							let model = _.clone(params)
							let hasIssues = false
							let bucket // This is the data on the lot we're reviewing on admin and entry
							if (props.interface === "entry") {
								bucket = props.data
							} else bucket = arcData
							let floorplanMatch = false
							_.forEach(model, (val, key) => {
								//? First check fp and elevation
								if (key === "floorplan") {
									if (bucket[key] && bucket[key] === v.properties[key]) {
										model[key] = true
										hasIssues = true
										floorplanMatch = true
									}
								} else if (key === "elevation") {
									if (
										bucket[key] &&
										bucket[key] == v.properties[key] &&
										bucket["elevation"] == v.properties["elevation"]
									) {
										model[key] = true
										hasIssues = true
									}
								} else {
									//? this is for everything else, do a string similarity check
									let val = bucket[key]
									if (
										val &&
										typeof val === "string" &&
										(val.toLowerCase() === "n/a" ||
											val.toLowerCase() === "na" ||
											val.toLowerCase() === "none")
									)
										return
									// If either the selected lot, or this iteration is a an int, itneeds to be translated.  These next two blocks basically teh same so we can DRY it someday
									if (typeof v.properties[key] === "number") {
										let lookupBucket = props.payload.public.arc_materials
										let colors = helpers.arcColorTypes()
										if (colors.includes(key)) {
											lookupBucket = props.payload.public.arc_colors
										}
										let lookup = _.find(lookupBucket, {
											id: v.properties[key],
										})
										if (lookup) {
											v.properties[key] = lookup.name
										}
									}
									if (typeof val === "number") {
										let lookupBucket = props.payload.public.arc_materials
										let colors = helpers.arcColorTypes()
										if (colors.includes(key)) {
											lookupBucket = props.payload.public.arc_colors
										}
										let lookup = _.find(lookupBucket, {
											id: val,
										})
										if (lookup) {
											val = lookup.name
										}
									}
									let similarScore = helpers.stringSimilarity(val, v.properties[key])
									if (similarScore > 90) {
										model[key] = true
										hasIssues = true
									}
								}
							})
							if (hasIssues) {
								let score = calculateSimilarityScore(model, dist, v.properties)
								issues.push({ lot: v.properties, score, model, distance: dist, floorplanMatch })
							}
						}
					}
				})
				issues = _.orderBy(issues, ["score"], ["desc"])
				_.forEach(issues, (v) => {
					let color
					if (v.score >= 0 && v.score < 30) color = "text-green-700"
					else if (v.score >= 30 && v.score < 60) color = "text-yellow-700"
					else if (v.score >= 60 && v.score < 80) color = "text-orange-400"
					else if (v.score >= 80 && v.score < 90) color = "text-orange-700"
					else if (v.score >= 90) color = "text-red-500"
					ret.push(
						<div
							key={v.lot.id}
							onClick={() => {
								setLot(v.lot)
							}}
							className="my-1 border border-dashed border-blue p-2 flex cursor-help"
							style={{ opacity: v.score < 30 ? 0.6 : 1 }}
							onMouseEnter={() => {
								setHoveringIssue(v.lot.id)
							}}
							onMouseLeave={() => {
								setHoveringIssue(false)
							}}>
							<div className={`flex-1 ${color} font-bold text-2xl`}>{v.score}</div>
							<div className="flex-4">
								<p className="text-blue font-bold pb-1 text-sm">{v.lot.address}</p>
								<div className="flex flex-wrap">
									{_.map(v.model, (val, key) => {
										if (val) {
											return (
												<p key={key} className="text-xs font-bold text-blue w-1/2 pr-2">
													<span className="font-bold text-red-600 text-xs mr-1">X</span>
													{helpers.snakeToTitle(key)}
												</p>
											)
										}
									})}
								</div>
							</div>
						</div>
					)
				})
			} catch (e) {
				console.log(e)
			}
		}
		return ret
	}

	//? Blocking system below fns
	const isSameSideOfStreet = (address1, address2) => {
		let streetName1 = address1.split(" ")[1]
		let streetName2 = address2.split(" ")[1]
		if (streetName1 !== streetName2) {
			return null
		}
		const num1 = parseInt(address1.split(" ")[0])
		const num2 = parseInt(address2.split(" ")[0])
		return num1 % 2 === num2 % 2
	}

	// Helper function to calculate "distance" between lots
	const calculateLotDistance = (lot1, lot2) => {
		if (lot1.section_id !== lot2.section_id) return Infinity
		if (lot1.block !== lot2.block) return Infinity
		return Math.abs(lot1.lot - lot2.lot)
	}

	const shortestDistanceBetweenPolygons = (coords1, coords2, units = "feet") => {
		// Create polygons from the coordinate sets
		const polygon1 = polygon(coords1)
		const polygon2 = polygon(coords2)

		// Find the closest point on polygon1 to the center of polygon2
		const centerPoint2 = center(polygon2)
		const closestPoint1 = nearestPointOnLine(
			lineString(explode(polygon1).features.map((f) => f.geometry.coordinates)),
			centerPoint2
		)

		// Find the closest point on polygon2 to the closest point on polygon1
		const closestPoint2 = nearestPointOnLine(
			lineString(explode(polygon2).features.map((f) => f.geometry.coordinates)),
			closestPoint1
		)

		// Calculate the distance between the two closest points
		const d = distance(point(closestPoint1.geometry.coordinates), point(closestPoint2.geometry.coordinates), {
			units: units,
		})

		return d
	}

	const checkLotMonotony = (lots, lotToCheck) => {
		//? Lots is the geojson maplots, lotToCheck is just the properties
		let hasBlocker = false
		let blockers = []
		for (const lot of lots) {
			let lotDetails = lot.properties
			if (lotDetails.id === lotToCheck.id) continue
			if (lotDetails.floorplan === lotToCheck.floorplan && lotDetails.elevation === lotToCheck.elevation) {
				const lotsAway = calculateLotDistance(lotToCheck, lotDetails)
				const sameSide = isSameSideOfStreet(lotToCheck.address, lotDetails.address)
				if (sameSide === null) {
					// Not on same street, skip
					//console.log("Not on same street", lotDetails.address, sameSide)
					continue
				}
				let varSameStreet = settings?.monotony?.sameStreet || 6
				let varOppositeStreet = settings?.monotony?.oppositeStreet || 3
				if (sameSide && lotsAway < varSameStreet) {
					hasBlocker = true
					lotDetails.reason = `Same side of street, ${lotsAway} lots away is less than ${varSameStreet}.`
					blockers.push(lotDetails)
					continue
				}
				/* let physicalDistance = distance(
					point(center(polygon(lot.geometry.coordinates)).geometry.coordinates),
					point(center(polygon(lotToCheck.coordinates)).geometry.coordinates),
					{ units: "feet" }
				) */
				let physicalDistance = shortestDistanceBetweenPolygons(lot.geometry.coordinates, lotToCheck.coordinates)
				let multiplier
				multiplier = (varOppositeStreet - 1) * lotToCheck.lot_size
				//? We have to use distance here.
				if (!sameSide && physicalDistance <= multiplier) {
					hasBlocker = true
					lotDetails.reason = `Distance of ${Math.round(physicalDistance)}ft is less than ${multiplier}ft.`
					blockers.push(lotDetails)
				}
			}
		}
		if (hasBlocker) {
			return blockers
		}
		return false // Pass: No conflicts found
	}

	const BuildBlocking = () => {
		if (blockers.length === 0) return null
		return (
			<>
				<h3>Blocking Issues</h3>
				{blockers}
			</>
		)
	}

	const buildBlockers = () => {
		if (!settings?.monotony?.hasBlocking) return null
		let ret = []
		let lot = _.find(geojson?.features, (v) => {
			return v.properties.id === props.id
		})
		if (lot) {
			try {
				let bucket // This is the data on the lot we're reviewing on admin and entry
				if (props.interface === "entry") {
					bucket = { ...lot.properties, ...props.data, coordinates: lot.geometry.coordinates }
				} else bucket = arcData
				if (!bucket.floorplan) return null
				let blocks = checkLotMonotony(geojson.features, bucket)
				if (!blocks) {
					setBlockers([])
					return null
				}

				_.forEach(blocks, (v) => {
					ret.push(
						<div
							key={v.id}
							onClick={() => {
								setLot(v)
							}}
							className="my-1 border border-dashed border-blue p-2 flex cursor-help"
							onMouseEnter={() => {
								setHoveringIssue(v.id)
							}}
							onMouseLeave={() => {
								setHoveringIssue(false)
							}}>
							<div className="flex-4">
								<p className="text-blue font-bold pb-1 text-sm text-red-600">
									{v.address}
									<span className="block text-xs mt-1 text-neutral-600">
										Same Floorplan + Elevation proximity. {v.reason}
									</span>
								</p>
							</div>
						</div>
					)
				})
				setBlockers(ret)
			} catch (e) {
				console.log(e)
			}
		}
	}

	const existingInfo = () => {
		if (!_.isEmpty(props.data)) {
			let ret = []
			let arcColors = helpers.arcColorTypes()
			let arcFields = helpers.arcFields()
			let nonColorFields = _.filter(arcFields, (field) => !arcColors.includes(field))
			_.forEach(arcColors, (v) => {
				if (props.data[v]) {
					if (typeof props.data[v] === "number") {
						let look = _.find(props.payload.public.arc_colors, { id: props.data[v] })
						if (look) {
							ret.push(
								<div key={v} className="text-[13px] pb-2 border-b border-neutral-200">
									<span className="text-blue font-bold">{helpers.snakeToTitle(v)}</span>
									<br />
									{look.name}
								</div>
							)
						}
					} else {
						ret.push(
							<div key={v} className="text-[13px] pb-2 border-b border-neutral-200">
								<span className="text-[13px] pb-2 font-bold text-blue">{helpers.snakeToTitle(v)}</span>
								<br />
								{props.data[v]}
							</div>
						)
					}
				}
			})
			_.forEach(nonColorFields, (v) => {
				if (props.data[v]) {
					if (typeof props.data[v] === "number") {
						let look = _.find(props.payload.public.arc_materials, { id: props.data[v] })
						if (look) {
							ret.push(
								<div key={v} className="text-[13px] pb-2 border-b border-neutral-200">
									<span className="text-blue font-bold">{helpers.snakeToTitle(v)}</span>
									<br />
									{look.name}
								</div>
							)
						}
					} else {
						ret.push(
							<div key={v} className="text-[13px] pb-2 border-b border-neutral-200">
								<span className="text-blue font-bold">{helpers.snakeToTitle(v)}</span>
								<br />
								{props.data[v]}
							</div>
						)
					}
				}
			})
			return (
				<div className="mb-4 pt-4">
					<h3 className="py-2 bg-blue text-white px-2 font-normal mb-2">Current Selections</h3>
					{ret}
				</div>
			)
		}
		return ""
	}

	/* if (!data) {
		return ""
	}
	let arcData = data.arc_data
	if (!arcData) return "" */

	return (
		<>
			<div className="flex h-full">
				<div className="flex-2 px-3 overflow-scroll max-h-[80vh]">
					<BuildBlocking />
					<h3>Potential Issues</h3>
					{buildIssues()}
				</div>
				<div className="flex-5 flex flex-col">
					<Legend />
					<div ref={mapContainer} className="w-full flex-1" />
				</div>
				<div className="flex-2 text-sm px-2">
					{existingInfo()}
					<h3 className="py-2 bg-blue text-white px-2 font-normal">Clicked Lot Info</h3>
					<InfoWindow
						lot={lot}
						openLot={(r) => {
							setModal(true)
						}}
					/>
					<LotModal
						open={modal}
						data={lot}
						onClose={() => {
							setModal(false)
						}}
					/>
				</div>
			</div>
		</>
	)
}

function mapStateToProps(state) {
	return {
		payload: state.payload,
		payloadStatus: state.payloadStatus,
		activeProperties: state.activeProperties,
	}
}

function matchDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			loadingStatus,
			getPayload,
		},
		dispatch
	)
}

export default connect(mapStateToProps, matchDispatchToProps)(ArcRepetitionMap)
